import type { Contact } from '../api.ts';

/** Usable for new call steps and notification recipients: not deleted, and active in the ERP data. */
export const isUsable = (c: Contact) => c.active && c.status === 'active';

/** Contract 1.3: a contact at a site either serves the site or the whole company (no site links). */
export const isCompanyContact = (c: Contact) => c.scope === 'company';

/** Roles to show for a contact at a site: its roles there, or its company-wide roles. */
export function rolesAt(c: Contact, siteId: string): string[] {
  if (isCompanyContact(c)) return c.companyRoles;
  return c.siteLinks.find((l) => l.site_id === siteId)?.roles ?? [];
}

/** Company contacts in display order: usable before inactive, company-level primaries first, then by name. */
export const byCompanyRole = (a: Contact, b: Contact) =>
  Number(isUsable(b)) - Number(isUsable(a)) ||
  Number(b.companyRoles.includes('primary')) - Number(a.companyRoles.includes('primary')) ||
  a.displayName.localeCompare(b.displayName);

function label(c: Contact) {
  return `${c.displayName}${c.jobTitle ? ` · ${c.jobTitle}` : ''}${c.companyRoles.includes('primary') ? ' · primary' : ''}`;
}

/** <option>s for a contact picker: the site's own contacts, then its company's company-wide contacts. */
export function ContactOptions({ contacts }: { contacts: Contact[] }) {
  const site = contacts.filter((c) => !isCompanyContact(c));
  const company = contacts.filter(isCompanyContact).sort(byCompanyRole);
  return (
    <>
      {site.length > 0 && (
        <optgroup label="Site contacts">
          {site.map((c) => (
            <option key={c.id} value={c.id}>
              {label(c)}
            </option>
          ))}
        </optgroup>
      )}
      {company.length > 0 && (
        <optgroup label="Company contacts">
          {company.map((c) => (
            <option key={c.id} value={c.id}>
              {label(c)}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}
